import React, { JSX } from 'react';
import { observer } from 'mobx-react';
import dayjs from 'dayjs';

import {
  faChevronDown,
  faChevronUp,
  faUser,
  faCalendarAlt,
  faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Col, Row } from 'reactstrap';

import { useTheme } from '@emotion/react';
import useTranslation from '../../Hooks/useTranslation';
import Keys from '../../../_Translations/generated/search-criteria-display.en.t.json.keys';
import fallback from '../../../_Translations/generated/search-criteria-display.en.t.json';
import { Nbsp } from '../UI/Nbsp';
import DateDisplay from '../UI/DateDisplay/DateDisplay';
import ServiceSearchParams from '../../SubModules/ServiceSearch/ServiceSearchParams';

export interface Props {
  searchParams: Partial<ServiceSearchParams>;
  regionName?: string | null;
  onModify: () => void;
  monthOnly?: boolean | false;
  isExpanded?: boolean | false;
}

const SearchCriteriaDisplay = observer(
  (props: Props): JSX.Element => {
    const { t } = useTranslation('search-criteria-display', fallback, Keys);

    const theme = useTheme();

    const { searchParams, isExpanded, monthOnly, onModify, regionName } = props;

    const numberOfAdults = searchParams.occupancy
      ? searchParams.occupancy.reduce((prev, cur) => {
          return prev + cur.adults;
        }, 0)
      : 0;

    const numberOfChildrenAndInfants = searchParams.occupancy
      ? searchParams.occupancy.reduce((prev, cur) => {
          return prev + cur.children + cur.infants;
        }, 0)
      : 0;

    const Region = (): JSX.Element => (
      <>
        {regionName && (
          <span className="mr-2">
            <FontAwesomeIcon icon={faMapMarkerAlt} />
            <Nbsp />
            {regionName}
          </span>
        )}
      </>
    );

    const TravelDates = (): JSX.Element | null => {
      if (monthOnly && searchParams.startDate) {
        return (
          <span className="mr-2">
            <FontAwesomeIcon icon={faCalendarAlt} />
            <Nbsp />
            {dayjs(searchParams.startDate).format('MMMM YYYY')}
          </span>
        );
      }
      if (searchParams.startDate || searchParams.endDate) {
        return (
          <>
            <span className="mr-2">
              <FontAwesomeIcon icon={faCalendarAlt} />
              <Nbsp />
              {searchParams.startDate && <DateDisplay date={searchParams.startDate} />}
              {searchParams.endDate && (
                <>
                  {' '}
                  - <DateDisplay date={searchParams.endDate} />
                </>
              )}
            </span>
          </>
        );
      }
      return null;
    };

    const Occupancy = (): JSX.Element => (
      <>
        {searchParams.occupancy && (
          <span className="mr-2">
            <FontAwesomeIcon icon={faUser} />
            <Nbsp />
            {`${numberOfAdults} ${t(Keys.adult, { count: numberOfAdults })}`}
            {numberOfChildrenAndInfants > 0 &&
              `, ${numberOfChildrenAndInfants} ${t(Keys.child, {
                count: numberOfChildrenAndInfants
              })}`}
            {` (${searchParams.occupancy.length} ${t(Keys.room, {
              count: searchParams.occupancy.length
            })})`}
          </span>
        )}
      </>
    );

    return (
      <Row onClick={(): void => onModify()}>
        <>
          <Col
            className="my-auto"
            sm={8}
            style={{
              color: theme.searchCriteriaDisplay.color,
              textDecoration: 'underline',
              cursor: 'pointer'
            }}
          >
            <Region />
            <TravelDates />
            <Occupancy />
          </Col>
          <Col className="d-xs-none">
            <Button
              type="button"
              outline
              onClick={(): void => onModify()}
              className="my-auto float-right"
            >
              {t(Keys.modify)}
              <Nbsp />
              <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
            </Button>
          </Col>
        </>
      </Row>
    );
  }
);

export default SearchCriteriaDisplay;
